// src/contexts/ProductsContext.js
import React, { createContext, useState, useEffect } from 'react';
import { collection, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

export const ProductsContext = createContext({
  products: [],
  imageItems: [],
  getCoverImage: () => null,
  loading: true,
});

export function ProductsProvider({ children }) {
  const [products, setProducts] = useState([]);
  const [imageItems, setImageItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch all products on mount
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'products'));
        const fetched = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setProducts(fetched);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Real-time listener for all imageItems
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'imageItems'), snap => {
      const allImages = snap.docs.map(d => {
        const x = d.data();
        return {
          id: d.id,
          caption: x.caption ?? '',
          imageURL: x.imageURL ?? '',
          productID: x.productID ?? null,
        };
      });
      setImageItems(allImages);
    });
    return () => unsub();
  }, []);

  // Helper: find cover image for a given product (similar to iOS ImageItemsManager)
  const getCoverImage = product => {
    if (!product) return null;

    // First try to find by productImage.imageItemID
    if (product.productImage?.imageItemID) {
      const coverImage = imageItems.find(img => img.id === product.productImage.imageItemID);
      if (coverImage) return coverImage;
    }

    // Fall back to finding by productID
    return imageItems.find(img => img.productID === product.id) || null;
  };

  const value = {
    products,
    imageItems,
    getCoverImage,
    loading,
  };

  return (
    <ProductsContext.Provider value={value}>
      {children}
    </ProductsContext.Provider>
  );
}
